import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/app/auth-context';
import {
  commitClientImport,
  previewClientImport,
  type ImportCommit,
  type ImportPreview,
} from '@/lib/api-client';

/**
 * CRM-143 — Head-coach CSV client-import page.
 *
 * Three-step flow:
 *   1. Drag-drop (or pick) a CSV file
 *   2. Preview: server validates rows + reports duplicates / unknown coaches
 *   3. Commit: server creates `clients` rows + dispatches invite emails
 *
 * Only the head_coach role sees this page. Other roles are redirected.
 *
 * @see CRM-143 Client Data Import (CSV)
 */
export function AdminImportClientsPage(): JSX.Element {
  const { user, accessToken } = useAuth();
  const { t } = useTranslation();

  const [filename, setFilename] = useState<string>('');
  const [csv, setCsv] = useState<string>('');
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [commitResult, setCommitResult] = useState<ImportCommit | null>(null);
  const [busy, setBusy] = useState<'preview' | 'commit' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isHeadCoach = user?.role === 'head_coach';

  const reset = useCallback(() => {
    setFilename('');
    setCsv('');
    setPreview(null);
    setCommitResult(null);
    setError(null);
  }, []);

  const onFile = useCallback(async (file: File) => {
    setError(null);
    setCommitResult(null);
    setPreview(null);
    try {
      const text = await file.text();
      setFilename(file.name);
      setCsv(text);
    } catch {
      setError(t('admin.import.errors.readFailed'));
    }
  }, [t]);

  const onPreview = useCallback(async () => {
    if (!user || !csv) return;
    setBusy('preview');
    setError(null);
    try {
      const r = await previewClientImport(accessToken, { csv, filename });
      setPreview(r);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setBusy(null);
    }
  }, [accessToken, csv, filename]);

  const onCommit = useCallback(async () => {
    if (!user || !csv) return;
    setBusy('commit');
    setError(null);
    try {
      const r = await commitClientImport(accessToken, { csv, filename });
      setCommitResult(r);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setBusy(null);
    }
  }, [accessToken, csv, filename]);

  if (!isHeadCoach) {
    return (
      <div className="mx-auto max-w-prose p-8 text-sm text-ink-secondary">
        {t('admin.import.notAuthorized')}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <header>
        <p className="text-xs uppercase tracking-wide text-ink-secondary">
          {t('admin.import.eyebrow')}
        </p>
        <h1 className="font-display text-3xl text-ink-primary">
          {t('admin.import.title')}
        </h1>
        <p className="mt-2 text-sm text-ink-secondary">
          {t('admin.import.subtitle')}
        </p>
      </header>

      <section
        className="rounded-md border border-dashed border-parchment-rule bg-parchment-raised p-8 text-center"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const file = e.dataTransfer.files[0];
          if (file) void onFile(file);
        }}
        data-testid="import-dropzone"
      >
        <label className="cursor-pointer text-sm font-medium text-flame underline">
          {t('admin.import.dropzone.choose')}
          <input
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void onFile(file);
            }}
          />
        </label>
        <p className="mt-2 text-xs text-ink-secondary">
          {t('admin.import.dropzone.hint')}
        </p>
        {filename && (
          <p className="mt-3 text-sm text-ink-primary">
            <strong>{t('admin.import.dropzone.selected')}:</strong> {filename}
          </p>
        )}
      </section>

      {filename && !commitResult && (
        <section className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => void onPreview()}
            disabled={busy !== null}
            className="rounded bg-flame-glow px-4 py-2 text-sm font-medium text-flame-ember disabled:opacity-50"
          >
            {busy === 'preview' ? t('admin.import.actions.previewing') : t('admin.import.actions.preview')}
          </button>
          {preview && preview.summary.records_valid > 0 && (
            <button
              type="button"
              onClick={() => void onCommit()}
              disabled={busy !== null}
              className="rounded bg-flame px-4 py-2 text-sm font-medium text-parchment disabled:opacity-50"
            >
              {busy === 'commit'
                ? t('admin.import.actions.committing')
                : t('admin.import.actions.commit', { count: preview.summary.records_valid })}
            </button>
          )}
          <button
            type="button"
            onClick={reset}
            className="text-sm text-ink-secondary underline"
          >
            {t('admin.import.actions.reset')}
          </button>
        </section>
      )}

      {error && (
        <div className="rounded-md border border-flame bg-flame-glow p-4 text-sm text-flame-ember">
          {error}
        </div>
      )}

      {preview && !commitResult && (
        <section className="rounded-md border border-parchment-rule bg-parchment p-4" data-testid="import-preview">
          <h2 className="text-sm font-semibold text-ink-primary">
            {t('admin.import.preview.title')}
          </h2>
          <ul className="mt-3 grid grid-cols-3 gap-3 text-center text-xs text-ink-secondary">
            <li>
              <strong className="block text-2xl text-ink-primary">{preview.summary.records_total}</strong>
              {t('admin.import.preview.total')}
            </li>
            <li>
              <strong className="block text-2xl text-emerald-700">{preview.summary.records_valid}</strong>
              {t('admin.import.preview.valid')}
            </li>
            <li>
              <strong className="block text-2xl text-flame-ember">{preview.summary.records_failed}</strong>
              {t('admin.import.preview.failed')}
            </li>
          </ul>
          {preview.sample.length > 0 && (
            <div className="mt-4">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-secondary">
                {t('admin.import.preview.sample')}
              </h3>
              <table className="mt-2 w-full text-left text-xs">
                <thead className="border-b border-parchment-rule text-ink-secondary">
                  <tr>
                    <th className="py-1">#</th>
                    <th>{t('admin.import.col.firstName')}</th>
                    <th>{t('admin.import.col.lastName')}</th>
                    <th>{t('admin.import.col.email')}</th>
                    <th>{t('admin.import.col.coach')}</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.sample.map((r) => (
                    <tr key={r.rowNumber} className="border-b border-parchment-rule/60">
                      <td className="py-1">{r.rowNumber}</td>
                      <td>{r.firstName}</td>
                      <td>{r.lastName}</td>
                      <td>{r.email}</td>
                      <td>{r.coachEmail}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {preview.errors.length > 0 && (
            <ErrorTable
              title={t('admin.import.preview.errors')}
              errors={preview.errors}
              tt={t}
            />
          )}
        </section>
      )}

      {commitResult && (
        <section className="rounded-md border border-parchment-rule bg-parchment p-4" data-testid="import-commit">
          <h2 className="text-sm font-semibold text-ink-primary">
            {t('admin.import.commit.title')}
          </h2>
          <p className="mt-2 text-sm text-ink-secondary">
            {t('admin.import.commit.summary', {
              success: commitResult.log.records_success,
              total: commitResult.log.records_total,
            })}
          </p>
          {commitResult.errors.length > 0 && (
            <ErrorTable
              title={t('admin.import.commit.errors')}
              errors={commitResult.errors}
              tt={t}
            />
          )}
          <button
            type="button"
            onClick={reset}
            className="mt-3 rounded bg-flame px-4 py-2 text-sm font-medium text-parchment"
          >
            {t('admin.import.actions.newImport')}
          </button>
        </section>
      )}
    </div>
  );
}

function ErrorTable({
  title,
  errors,
  tt,
}: {
  title: string;
  errors: { rowNumber: number; email: string; reason: string; message: string }[];
  tt: (k: string) => string;
}): JSX.Element {
  return (
    <div className="mt-4">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-secondary">{title}</h3>
      <table className="mt-2 w-full text-left text-xs">
        <thead className="border-b border-parchment-rule text-ink-secondary">
          <tr>
            <th className="py-1">#</th>
            <th>{tt('admin.import.col.email')}</th>
            <th>{tt('admin.import.col.reason')}</th>
            <th>{tt('admin.import.col.detail')}</th>
          </tr>
        </thead>
        <tbody>
          {errors.map((e, idx) => (
            <tr key={`${e.rowNumber}-${idx}`} className="border-b border-parchment-rule/60">
              <td className="py-1">{e.rowNumber}</td>
              <td>{e.email || '—'}</td>
              <td className="text-flame-ember">{e.reason}</td>
              <td>{e.message}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
